import { Injectable, signal } from '@angular/core';

export interface StagedValueUpdate {
  inputValue: number | null;
  originalInputValue: number | null;
  timestamp: Date;
}

export interface MeasureValueDraft {
  updates: Map<number, StagedValueUpdate>;
  creates: Map<string, StagedValueUpdate>;
}

const EMPTY_DRAFT: MeasureValueDraft = {
  updates: new Map(),
  creates: new Map(),
};

/**
 * Staged edits and creates of automatic-measure values, keyed by measure id.
 * Creates are keyed by the row's ISO timestamp (grid rows generated for an
 * expected-but-missing date have no id until they are saved). Root-provided
 * on purpose: the draft survives navigation and is cleared only on save or
 * cancel (parity with the campaign measures grid).
 */
@Injectable({ providedIn: 'root' })
export class AutomaticValueDraftService {
  private readonly drafts = signal<Map<number, MeasureValueDraft>>(new Map());

  draftFor(measureId: number): MeasureValueDraft {
    return this.drafts().get(measureId) ?? EMPTY_DRAFT;
  }

  hasDraft(measureId: number): boolean {
    const draft = this.draftFor(measureId);
    return draft.updates.size > 0 || draft.creates.size > 0;
  }

  updateCount(measureId: number): number {
    return this.draftFor(measureId).updates.size;
  }

  createCount(measureId: number): number {
    return this.draftFor(measureId).creates.size;
  }

  stagedUpdate(measureId: number, valueId: number): StagedValueUpdate | undefined {
    return this.draftFor(measureId).updates.get(valueId);
  }

  stagedCreate(measureId: number, isoTimestamp: string): StagedValueUpdate | undefined {
    return this.draftFor(measureId).creates.get(isoTimestamp);
  }

  /** Stages a value edit; editing back to the original value unstages it. */
  stageUpdate(measureId: number, valueId: number, entry: StagedValueUpdate): void {
    this.mutate(measureId, draft => {
      if (entry.inputValue === entry.originalInputValue) {
        draft.updates.delete(valueId);
      } else {
        draft.updates.set(valueId, entry);
      }
    });
  }

  /** Stages a create on a virtual (expected-but-missing) row; clearing back to null unstages it. */
  stageCreate(measureId: number, isoTimestamp: string, entry: StagedValueUpdate): void {
    this.mutate(measureId, draft => {
      if (entry.inputValue === null) {
        draft.creates.delete(isoTimestamp);
      } else {
        draft.creates.set(isoTimestamp, entry);
      }
    });
  }

  /** Drops staged entries whose row sits inside one of the deleted spans. The bulk save
   *  refuses the whole batch when a single id no longer exists, so one stale entry would
   *  make every other edit unsaveable. */
  dropWithin(measureId: number, spans: readonly { from: Date; to: Date }[]): void {
    if (spans.length === 0) return;
    const inside = (at: Date): boolean => spans.some(span => at >= span.from && at <= span.to);
    this.mutate(measureId, draft => {
      for (const [id, entry] of draft.updates) {
        if (inside(entry.timestamp)) draft.updates.delete(id);
      }
      for (const [iso, entry] of draft.creates) {
        if (inside(entry.timestamp)) draft.creates.delete(iso);
      }
    });
  }

  clear(measureId: number): void {
    if (!this.drafts().has(measureId)) return;
    const next = new Map(this.drafts());
    next.delete(measureId);
    this.drafts.set(next);
  }

  private mutate(measureId: number, change: (draft: MeasureValueDraft) => void): void {
    const current = this.draftFor(measureId);
    const draft: MeasureValueDraft = {
      updates: new Map(current.updates),
      creates: new Map(current.creates),
    };
    change(draft);
    const next = new Map(this.drafts());
    if (draft.updates.size === 0 && draft.creates.size === 0) {
      next.delete(measureId);
    } else {
      next.set(measureId, draft);
    }
    this.drafts.set(next);
  }
}
